/**
 * Presentation helpers shared across the wallet UI. Pure, no UI deps.
 */

/** Truncate a long address/hash for display, keeping head + tail. */
export function truncateMiddle(value: string, head = 6, tail = 6): string {
  if (value.length <= head + tail + 1) return value;
  return `${value.slice(0, head)}…${value.slice(-tail)}`;
}

/**
 * Human-readable label + one-line explanation for a delegation capability.
 * The frozen capability set is {vault-agreement, ddc-read, ddc-write,
 * gar-record, invite-email}; unknown codes fall back to the raw code so a new
 * capability is never silently hidden from the consent screen.
 */
const CAPABILITY_COPY: Record<string, { label: string; detail: string }> = {
  'ddc-read': { label: 'Read your data', detail: 'View files stored in your vault' },
  'ddc-write': { label: 'Write data', detail: 'Save and update files in your vault' },
  'vault-agreement': { label: 'Connect to your vault', detail: 'Establish an agreement to use your vault' },
  'gar-record': { label: 'Record usage', detail: 'Write usage records on your behalf' },
  'invite-email': { label: 'Send invite emails', detail: 'Email invite links to people you invite' },
};

export function capabilityLabel(code: string): string {
  return CAPABILITY_COPY[code]?.label ?? code;
}

export function capabilityDetail(code: string): string | null {
  return CAPABILITY_COPY[code]?.detail ?? null;
}

export function isKnownCapability(code: string): boolean {
  return code in CAPABILITY_COPY;
}

/** Format a duration in seconds as a short human phrase ("1 hour", "30 minutes"). */
export function formatDuration(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds <= 0) return '0 seconds';
  const units: Array<[string, number]> = [
    ['day', 86400],
    ['hour', 3600],
    ['minute', 60],
    ['second', 1],
  ];
  for (const [name, size] of units) {
    if (seconds % size === 0 && seconds >= size) {
      const n = seconds / size;
      return `${n} ${name}${n === 1 ? '' : 's'}`;
    }
  }
  // Non-exact: show the largest whole unit (rounded down) plus remainder hint.
  for (const [name, size] of units) {
    if (seconds >= size) {
      const n = Math.floor(seconds / size);
      return `about ${n} ${name}${n === 1 ? '' : 's'}`;
    }
  }
  return `${seconds} seconds`;
}
